import * as React from "react";
import * as _ from "lodash";
import moment from "moment-timezone";
import ReactTable, {
  CellInfo,
  Column,
  Filter,
  Instance,
  TableCellRenderer,
  TableProps
} from "react-table";
import styled from "styled-components";

import {
  FilterParams,
  PaginatedParams,
  IApiError,
  formatFilterObject,
  formatPaginationObject
} from "src/library/utils";
import {
  useLocalStorage,
  useWindowSize
} from "src/library/hooks";

import Grid from "../Hedron";
import {
  ActionButton,
  Button,
  IButtonProps
} from "../Button";
import FiltersClear from "../Filters/FiltersClear";
import { Select } from "../Forms";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { Text } from "../Text";
import { IAction } from "../Button/ActionButton";
import {
  CheckboxLabel,
  CheckboxStyled
} from "../Forms/Checkbox";
import { Icons } from "../Icon/types";
import TablePagination, { ITablePaginationRefProps } from "./TablePagination";
import TableNoData from "./TableNoData";
import TableLoading from "./TableLoading";

export interface FilterType<T> {
  text: string;
  value: string;
  filter: FilterParams<T>;
}

export interface BulkSelectState {
  [key: number]: number;
}

export interface TableTopLevelButton extends Omit<IButtonProps, "children"> {
  title: string;
  onClick: () => void;
}

export interface ITableProps<T = any> extends Partial<TableProps> {
  tableId: string; // used for knowing when to reset
  tableOf: string;
  totalRows?: number;
  view?: ViewType;
  viewAllowedToToggle?: boolean;
  error?: IApiError | null;
  // TODO: This prop seems to be legacy (it is not used).
  clearFilters?: () => void;
  columns: CustomTableColumn[];
  mainAction?: (row: CellInfo) => void;
  quickFilters?: Array<FilterType<T>>;
  disabledQuickFilters?: string;
  additionalFilters?: any;
  onFetchData: (filters: FilterParams<T>, pagination: PaginatedParams<T>) => void;
  defaultPagination?: PaginatedParams<T>;
  defaultFilters?: FilterParams<T>;
  data?: any[] | never[];
  bulkActionOptions?: (bulkState: BulkSelectState) => IAction[];
  bulkActionsIdColumn?: string;
  filterNaturalLanguage?: string;
  canBulkSelectRow?: (data: T) => boolean;
  bulkActionsLoading?: boolean;
  topLevelButtons?: TableTopLevelButton[];
}

export interface ITableInstance<T = any> {
  props: ITableProps<T>;
  reloadTable: (force?: boolean, filters?: FilterParams<T>) => void;
  resetPagination: () => void;
}

interface TableColumnsAvailable {
  [P: string]: boolean;
}
interface TableSettingState {
  [P: string]: TableSettings;
}

interface TableSettings {
  id: string;
  lastActive: Date;
  columns: TableColumnsAvailable;
  settings: {
    sort: TableProps["sorted"];
    pageSize: number;
  };
  state: {
    page: number;
    filters: FilterParams<any>;
  };
}

export type ViewType = "list" | "grid";

export interface CustomTableColumn extends Column {
  mainAction?: (row: CellInfo) => void;
  mobile?: boolean;
}
/**
 * ReactTable custom component (wrapper) for consist tables
 */
export const Table: React.ForwardRefRenderFunction<unknown, ITableProps> = (
  {
    tableOf,
    totalRows,
    view,
    viewAllowedToToggle,
    error,
    filterable,
    clearFilters,
    columns,
    mainAction,
    additionalFilters,
    disabledQuickFilters,
    quickFilters,
    onFetchData,
    defaultPagination,
    defaultFilters,
    defaultFiltered,
    data,
    className,
    loading,
    pages,
    bulkActionOptions,
    bulkActionsLoading,
    tableId,
    showPagination,
    showFilters: showTableFilters,
    filterNaturalLanguage,
    canBulkSelectRow,
    bulkActionsIdColumn,
    pageSizeOptions,
    topLevelButtons,
    ...tableProps
  },
  ref
) => {
  const [ refreshAvailable, setRefreshAvailable ] = React.useState(false);
  const TableRef = React.useRef<Instance<any>>(null);
  const PaginationRef = React.useRef<ITablePaginationRefProps>(null);
  const [ viewType, setViewType ] = React.useState<ViewType>(view || "list");
  const [ showFilters, setShowFilters ] = React.useState<boolean>(view === "grid" ? false : true);
  const [ showTablePreferences, setShowTablePreferences ] = React.useState<boolean>(false);
  const [ quickFilterParams, setQuickFilterParams ] = React.useState<FilterParams<any>>({});
  const [ columnsState, setColumns ] = React.useState<CustomTableColumn[]>([]);
  const { width } = useWindowSize();
  // bulk action state
  const [ bulkSelectAllState, setBulkSelectAllState ] = React.useState<boolean>(false);
  const [ bulkSelectState, setBulkSelectState ] = React.useState<BulkSelectState>({});
  const [ defaultFilteredData, setDefaultFilteredData ] = React.useState<Filter[] | []>(defaultFiltered || []);
  const hasBulkActions = React.useMemo(() => bulkActionOptions && bulkActionOptions(bulkSelectState).length > 0, [ bulkActionOptions, bulkSelectState ]);

  // Handles our data
  const tableData = React.useMemo(() => {
    if (data && data.length > 0 && !error) {
      if (data && hasBulkActions) {
        return _.map(data, x => ({
          ...x,
          selected: bulkSelectState && x[ bulkActionsIdColumn || "id" ] && bulkSelectState[ x[ bulkActionsIdColumn || "id" ] ]
        }));
      }

      return data;
    } else {
      return undefined;
    }
  }, [
    bulkActionsIdColumn,
    bulkSelectState,
    data,
    error,
    hasBulkActions
  ]);

  /**
   * Classnames
   */
  const tableClass = React.useMemo(() => {
    // Set the className prop
    let newClassName = className || "";

    newClassName += loading ? " loading" : "";
    newClassName += !tableData && !loading ? " no-data" : "";
    newClassName += showFilters ? " show-filters" : " hide-filters";

    return newClassName;
  }, [
    className,
    loading,
    showFilters,
    tableData
  ]);

  /**
   * bulk action state
   */
  React.useEffect(() => {
    if (hasBulkActions && tableData) {
      const countPageSelect = _.filter(tableData, row => row.selected).length;

      setBulkSelectAllState(countPageSelect === tableData.length);
    }
  }, [ tableData, hasBulkActions ]);

  // table settings
  const [ savedTableSettings, saveTableSettings ] = useLocalStorage("table-settings");
  const [ tableSettingsData, setTableSettings ] = React.useState<TableSettings | null>(null);
  const [ hasLoadedSettings, setHasLoadedSettings ] = React.useState(false);

  React.useEffect(() => {
    // set the initail settings from local storage
    const tableSettingsState: TableSettingState = savedTableSettings ? JSON.parse(savedTableSettings) : {};
    let tableSetting: TableSettings;
    let availableColumns: { [P: string]: boolean } = {};

    // has existing table settings
    if (tableSettingsState[ tableOf ]) {
      // handle to columns visibility
      availableColumns = tableSettingsState[ tableOf ].columns;

      tableSetting = {
        ...tableSettingsState[ tableOf ],
        columns: availableColumns
      };

      // if same table id then just reload from there
      // and set the table settings
      if (
        tableSettingsState[ tableOf ].id !== tableId ||
        moment
          .utc(tableSettingsState[ tableOf ].lastActive)

          .isBefore(moment.utc().subtract(1, "hour"))
      ) {
        setHasLoadedSettings(false);
        tableSetting.id = tableId;

        tableSetting.state = {
          page: 0,
          filters: defaultFilters || {}
        };
      }

      tableSetting.lastActive = new Date();
      // if its a brand new table
    } else {
      // set the column availablity
      _.each(columns, column => {
        if (typeof column.Header === "string") {
          // show by default
          availableColumns[ column.Header ] = typeof column.show === "boolean" ? column.show : true;
        }
      });

      // set the table settings state
      tableSetting = {
        id: tableId,
        lastActive: new Date(),
        columns: availableColumns,
        settings: {
          pageSize: tableProps.pageSize || defaultPagination?.limit || 10,
          sort: []
        },
        state: {
          page: 0,
          filters: defaultFilters || {}
        }
      };
    }

    setTableSettings(tableSetting);
  }, [
    width,
    tableId,
    defaultPagination
  ]);

  // save changes to local storage
  React.useEffect(() => {
    const currentTableSettings: TableSettingState = savedTableSettings ? JSON.parse(savedTableSettings) : {};

    if (!_.isEmpty(tableSettingsData)) {
      saveTableSettings(JSON.stringify({
        ...currentTableSettings,
        [ tableOf ]: tableSettingsData
      }));
    }

    if (!hasLoadedSettings) {
      setHasLoadedSettings(true);
    }
  }, [ tableSettingsData ]);

  const reloadTable = React.useCallback(
    (force = false, overrideFilters?: FilterParams<any>) => {
      if (TableRef.current && onFetchData && TableRef.current.state) {
        if (tableProps.manual === false) {
          if (force) {
            onFetchData(
              formatFilterObject(TableRef.current.state.filtered, {
                ...(defaultFilters || {}),
                ...quickFilterParams,
                ...overrideFilters
              }),
              formatPaginationObject(TableRef.current.state, defaultPagination)
            );
            // Clear the selected rows.
            setBulkSelectAllState(false);
            setBulkSelectState({});
          }
        } else {
          onFetchData(
            formatFilterObject(TableRef.current.state.filtered, {
              ...(defaultFilters || {}),
              ...quickFilterParams,
              ...overrideFilters
            }),
            formatPaginationObject(TableRef.current.state, defaultPagination)
          );
          // Clear the selected rows.
          setBulkSelectAllState(false);
          setBulkSelectState({});
        }
      }
    },
    [
      defaultFilters,
      defaultPagination,
      quickFilterParams,
      onFetchData,
      tableProps.manual
    ]
  );

  React.useEffect(() => {
    if (TableRef.current && tableSettingsData) {
      TableRef.current.setState({ page: tableSettingsData.state.page }, () => {
        reloadTable(true);
        setRefreshAvailable(true);
      });
    }
  }, [ quickFilterParams, hasLoadedSettings ]);

  /**
   * Toggle the select row
   *
   * @param data - An array of data
   * @param attr - An attribute from the data array
   */
  const toggleRow = React.useCallback(
    (attr: any) => {
      const selected = { ...bulkSelectState };

      // Check if the id already exists in our array
      if (selected[ attr ] === undefined) {
        selected[ attr ] = attr;
      } else {
        delete selected[ attr ];
      }

      // Update the selected rows state
      setBulkSelectState(selected);
    },
    [ bulkSelectState ]
  );

  /**
   * Toggle all of the rows
   *
   * @param data - An array of data
   * @param attr - An attribute from the data array
   */
  const toggleAll = React.useCallback(
    (attribute: any) => {
      const selected = { ...bulkSelectState };

      if (tableData) {
        // Check whether the select all is already checked
        if (!bulkSelectAllState) {
          // Add each item to the selected array
          tableData.forEach(item => {
            if ((canBulkSelectRow && canBulkSelectRow(item)) || !canBulkSelectRow) {
              selected[ item[ attribute ] ] = item[ attribute ];
            }
          });
        } else {
          tableData.forEach(item => {
            delete selected[ item[ attribute ] ];
          });
        }
      }

      setBulkSelectState(selected);
    },
    [
      tableData,
      bulkSelectState,
      bulkSelectAllState
    ]
  );

  React.useEffect(() => {
    if (tableSettingsData) {
      const columnData = tableSettingsData.columns;

      const checkboxCol = hasBulkActions ?
        [
          checkbox({
            name: "Bulk Actions",
            id: "bulk-Actions",
            show: hasBulkActions,
            header: <CheckboxStyled
              className="checkbox"
              checked={bulkSelectAllState}
              onChange={() => toggleAll(bulkActionsIdColumn || "id")}
                    />,
            cell: ({ original }) => {
              return (
                <CheckboxStyled
                  disabled={canBulkSelectRow ? !canBulkSelectRow(original) : false}
                  type="checkbox"
                  className="checkbox"
                  checked={original.selected}
                  onChange={() => toggleRow(original[ bulkActionsIdColumn || "id" ])}
                />
              );
            }
          })
        ] :
        [];

      setColumns(
        _.concat(
          checkboxCol,
          _.map(columns, col => {
            const header = col.Header;

            col = {
              ...col,

              className: `${col.accessor}-column column`,
              sortable: viewType === "grid" ? false : col.sortable
            };

            if (viewType === "grid" && (col.accessor === "grid" || col.filterable !== false)) {
              return {
                ...col,
                show: true
              };
            }

            if (viewType === "grid") {
              return {
                ...col,
                show: typeof col.show === "boolean" ? col.show : true
              };
            }

            if (viewType === "list" && col.accessor === "grid") {
              return {
                ...col,
                show: false
              };
            }

            return {
              ...col,
              show: typeof header === "string" && typeof columnData[ header ] === "boolean" ? columnData[ header ] : true
            };
          })
        )
      );
    }
  }, [
    tableSettingsData,
    viewType,
    TableRef.current,
    tableData,
    bulkSelectAllState,
    columns
  ]);

  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  React.useImperativeHandle(ref, () => ({
    reloadTable<T>(force = false, overrideFilters: FilterParams<T> = {}) {
      reloadTable(force, overrideFilters);
    },
    resetPagination() {
      if (PaginationRef.current) {
        PaginationRef.current.onPageChange(0);
      }
    },
    props: {
      ...TableRef.current?.props,
      page: PaginationRef.current?.currentPage
    }
  }));

  const toggleView = React.useCallback(() => {
    if (viewType === "grid") {
      setViewType("list");
    } else {
      setShowFilters(false);
      setViewType("grid");
    }
  }, [
    viewType,
    setViewType,
    setShowFilters
  ]);

  const setQuickFilters = React.useCallback(
    (filtered: FilterParams<any>) => {
      if (TableRef.current) {
        TableRef.current.setState(
          { page: 0 },
          () => {
            setQuickFilterParams(filtered);
          }
        );
      }
    },
    [ TableRef ]
  );

  return (
    <>
      <Grid.Bounds
        direction="vertical"
        width="100%"
      >
        <Grid.Bounds direction={width && width < 960 ? "vertical" : "horizontal"}>
          {((bulkActionOptions && hasBulkActions && bulkSelectState) || topLevelButtons) && (
            <Grid.Box className="bulk-actions-wrapper">
              {bulkActionOptions && (
                <ActionButton
                  disabled={_.isEmpty(bulkSelectState)}
                  loading={bulkActionsLoading || loading}
                  title="Bulk actions"
                  items={bulkActionOptions(bulkSelectState)}
                />
              )}

              {topLevelButtons && (
                topLevelButtons.map((button, index) => (
                  <div
                    className="top-level-button"
                    key={`${index}-${button.title.replace(/\s/g, "_")}`}
                  >
                    <Button
                      onClick={() => button.onClick()}
                      loading={button.loading}
                      disabled={button.disabled}
                    >
                      {button.title}
                    </Button>
                  </div>
                ))
              )}
            </Grid.Box>
          )}

          {(quickFilters || additionalFilters) && (
            <Grid.Box shiftRight={width && width < 960 ? false : true}>
              <Grid.Bounds
                width="100%"
                direction={width && width < 960 ? "vertical" : "horizontal"}
              >
                {additionalFilters && (
                  <AdditionalFiltersWrapper width="100%">
                    {additionalFilters}
                  </AdditionalFiltersWrapper>
                )}

                {quickFilters && (
                  <Grid.Box width="100%">
                    <Select
                      handleChange={e => {
                        if (quickFilters) {
                          const filterType = _.find(quickFilters, filterType => filterType.value === e.target.value);

                          if (filterType) {
                            setQuickFilters(filterType.filter);
                          }
                        }
                      }}
                      as="select"
                      options={quickFilters}
                      name="quick-filters"
                      label="Quick filters"
                      disabled={!!disabledQuickFilters}
                      labelTooltip={disabledQuickFilters}
                    />
                  </Grid.Box>
                )}
              </Grid.Bounds>
            </Grid.Box>
          )}
        </Grid.Bounds>

        <div className={`ReactTable-container ${viewType}-view`}>
          {(typeof showTableFilters !== "boolean" || showTableFilters) && (
            <Grid.Bounds
              direction="horizontal"
              valign="center"
              className="table-toggles"
            >
              <Grid.Box>
                {!!filterNaturalLanguage && filterNaturalLanguage !== "" && (
                  <AppliedDefaultFiltersWrapper>
                    <Text
                      weight="bold"
                      fontSize="h4"
                      style={{ float: "left" }}
                    >
                      {filterNaturalLanguage}
                    </Text>
                  </AppliedDefaultFiltersWrapper>
                )}
              </Grid.Box>

              <Grid.Box className="toggle-right">
                {TableRef.current &&
                  ((TableRef.current.state.filtered && TableRef.current.state.filtered.length > 0) ||
                    (filterNaturalLanguage && filterNaturalLanguage.length >= 1)) && (
                      <Grid.Box>
                        <FiltersClear
                          clearFilters={() => {
                            if (defaultFilteredData.length > 0) {
                              setDefaultFilteredData([]);
                            }

                            if (clearFilters && filterNaturalLanguage !== "") {
                              clearFilters();
                            }

                            if (TableRef.current) {
                              TableRef.current.setState({ filtered: [] }, () => {
                                reloadTable(true);
                              });
                            }
                          }}
                        />
                      </Grid.Box>
                )}

                {refreshAvailable && (
                  <Grid.Box
                    className="toggle-view"
                    onClick={() => reloadTable(true)}
                  >
                    <Icon
                      className="view-refresh-icon"
                      $fillColor="primaryDark"
                      icon={Icons.REFRESH}
                      size={20}
                    />
                  </Grid.Box>
                )}

                {viewType === "list" && (
                  <>
                    <Grid.Box
                      className={`filters-hide-show ${showFilters ? "active" : ""}`}
                      title={`${showFilters ? "Hide" : "Show"} filters`}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Icon
                        icon={Icons.FILTER}
                        size={20}
                        $fillColor="primaryDark"
                      />

                      <Text
                        className="toggle-text"
                        weight="bold"
                      >
                        {showFilters ? "Hide" : "Show"}

                        {" "}
                        filters
                      </Text>
                    </Grid.Box>
                  </>
                )}

                {viewType === "list" && (
                  <Grid.Box
                    className="toggle-view"
                    onClick={() => setShowTablePreferences(!showTablePreferences)}
                  >
                    <Icon
                      className="view-toggle-icon"
                      $fillColor="primaryDark"
                      icon={Icons.SETTINGS}
                      size={20}
                    />

                    <Text
                      className="toggle-text"
                      weight="bold"
                    >
                      Table preferences
                    </Text>
                  </Grid.Box>
                )}

                {viewAllowedToToggle && view !== undefined && (
                  <Grid.Box
                    className="toggle-view"
                    onClick={() => toggleView()}
                  >
                    <Icon
                      className="view-toggle-icon"
                      $fillColor="primaryDark"
                      icon={viewType === "list" ? Icons.GRID : Icons.TABLE}
                      size={20}
                    />

                    <Text
                      className="toggle-text"
                      weight="bold"
                    >
                      {`Switch to ${viewType === "list" ? "grid" : "list"} view`}
                    </Text>
                  </Grid.Box>
                )}
              </Grid.Box>
            </Grid.Bounds>
          )}

          {hasLoadedSettings && (
            // @ts-ignore
            <ReactTable
              ref={TableRef}
              {...tableProps}
              pages={pages}
              defaultSorted={tableSettingsData ? tableSettingsData.settings.sort : []}
              defaultFiltered={defaultFilteredData}
              onSortedChange={sort => {
                if (tableSettingsData) {
                  setTableSettings({
                    ...tableSettingsData,
                    settings: {
                      ...tableSettingsData.settings,
                      sort
                    }
                  });
                }
              }}
              onPageSizeChange={(pageSize, page) => {
                if (tableSettingsData) {
                  setTableSettings({
                    ...tableSettingsData,
                    state: {
                      ...tableSettingsData.state,
                      page
                    },
                    settings: {
                      ...tableSettingsData.settings,
                      pageSize
                    }
                  });
                }
              }}
              onPageChange={page => {
                if (tableSettingsData) {
                  setTableSettings({
                    ...tableSettingsData,
                    state: {
                      ...tableSettingsData.state,
                      page
                    }
                  });
                }
              }}
              showFilters={typeof showTableFilters === "boolean" ? showTableFilters : true}
              showPagination={typeof showPagination === "boolean" ? showPagination : true}
              showPaginationTop={false}
              showPaginationBottom={typeof showPagination === "boolean" ? showPagination : true}
              pageSizeOptions={pageSizeOptions || [
                10,
                20,
                30,
                40,
                50,
                100
              ]}
              // @ts-ignore
              defaultPage={tableSettingsData ? tableSettingsData.state.page : 0}
              defaultPageSize={tableSettingsData ? tableSettingsData.settings.pageSize : defaultPagination ? defaultPagination.limit : 10}
              // Set the minRos prop
              minRows={0}
              loading={loading}
              // Set the filterable prop
              filterable={typeof filterable === "boolean" ? filterable : true}
              // Set the default table components
              LoadingComponent={TableLoading}
              NoDataComponent={() => (
                <TableNoData
                  error={error}
                  onRetry={() => reloadTable(true)}
                />
              )}
              PaginationComponent={paginationProps => {
                return (
                  <TablePagination
                    ref={PaginationRef}
                    totalRows={tableProps.manual ? (tableData ? tableData.length : 0) : totalRows || 0}
                    pageSize={tableSettingsData?.settings.pageSize}
                    {...paginationProps}
                  />
                );
              }}
              className={tableClass}
              ResizerComponent={props => {
                return (
                  <Icon
                    {...props}
                    className="rt-resizer"
                    icon={Icons.HORIZONTALDRAG}
                    size={20}
                    $fillColor="lightGrey"
                  />
                );
              }}
              data={tableData}
              manual={tableProps.manual || true}
              onFetchData={() => {
                reloadTable(true);
              }}
              // @ts-ignore
              getTrProps={(_u, row) => ({
                onClick: () => {
                  const hasActions = columnsState.find(col => col.id === "actions");

                  if (hasActions) {
                    if (width && width < 960) {
                      if (mainAction && viewType === "list") {
                        mainAction(row);
                      }
                    }
                  }
                }
              })}
              columns={_.map(columnsState, column => {
                return {
                  ...column,
                  className: `${column.accessor}-column column`,
                  sortable: viewType === "grid" ? false : column.sortable,
                  show: column.mobile === false && width && width < 960 ? false : column.show
                };
              })}
            />
          )}

          {tableSettingsData && (
            <Modal
              small
              show={showTablePreferences}
              title={`${tableOf} table preferences`}
              body=""
              backgroundAction={() => setShowTablePreferences(false)}
              content={(
                <>
                  {_.map(tableSettingsData.columns, (checked, name) => {
                    return (
                      <CheckboxLabel
                        className="checkbox-label"
                        for={`column-${name}`}
                        label={name}
                        key={`col-checkbox-${name}`}
                      >
                        <CheckboxStyled
                          name={`column-${name}`}
                          id={`column-${name}`}
                          checked={checked}
                          onChange={() => {
                            setTableSettings({
                              ...tableSettingsData,
                              columns: {
                                ...tableSettingsData.columns,
                                [ name ]: !checked
                              }
                            });
                          }}
                        />
                      </CheckboxLabel>
                    );
                  })}
                </>
              )}
            />
          )}
        </div>
      </Grid.Bounds>
    </>
  );
};

const checkbox = ({
  header, cell, id, show
}: ICheckboxProps): CustomTableColumn => {
  return {
    Header: header,
    id,
    headerStyle: { textAlign: "center" },
    style: { textAlign: "center" },
    width: 80,
    sortable: false,
    filterable: false,
    Cell: cell,
    show
  };
};

interface ICheckboxProps {
  name: string;
  header: React.ReactElement;
  cell: TableCellRenderer;
  id: string;
  show?: boolean;
}

const AdditionalFiltersWrapper = styled(Grid.Box)`
  @media (max-width: 960px) {
    margin-bottom: 15px;
  }
`;

export default React.forwardRef(Table);

const AppliedDefaultFiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: ${({ theme }) => theme.spacing.padding.md};

  span {
    padding-right: ${({ theme }) => theme.spacing.padding.xs};
  }
`;
